import { FormEvent, useCallback, useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Skeleton } from '../ui/skeleton';
import RichTextField from './RichTextField';
import { uploadPendingAttachment } from '../../lib/attachments';
import { apiErrorMessage, apiFetch, v1Path } from '../../lib/api';
import { isProjectDto, type ProjectDto } from '../../lib/work-types';
import { useOverlays } from './overlay-context';
import { PageTitle, WorkPageError } from './page-states';

export default function ProjectGeneralSettings({
  projectId,
  canManage,
}: {
  projectId: string;
  canManage: boolean;
}) {
  const { toast, confirm } = useOverlays();
  const [project, setProject] = useState<ProjectDto | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const projectPath = v1Path(`/projects/${projectId}`);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch<unknown>(projectPath);
      if (!isProjectDto(res)) {
        throw new Error('Malformed stream response.');
      }
      setProject(res);
      setName(res.name);
      setDescription(res.description ?? '');
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to load stream'));
      setProject(null);
    } finally {
      setLoading(false);
    }
  }, [projectPath]);

  useEffect(() => {
    void load();
  }, [load]);

  const archived = Boolean(project?.archived_at);
  const editable = canManage && !archived;
  const dirty =
    project !== null &&
    (name.trim() !== project.name || description !== (project.description ?? ''));

  async function onSave(event: FormEvent) {
    event.preventDefault();
    if (!project || !dirty) return;
    if (!name.trim()) {
      setError('Name is required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await apiFetch(projectPath, {
        method: 'PATCH',
        body: JSON.stringify({ name: name.trim(), description }),
      });
      toast('Stream settings saved');
      await load();
    } catch (err) {
      const message = apiErrorMessage(err, 'Failed to save stream settings');
      setError(message);
      toast(message);
    } finally {
      setSaving(false);
    }
  }

  async function onArchive() {
    if (!project) return;
    const ok = await confirm({
      title: 'Archive stream',
      description: `Archive ${project.name}? It disappears from the stream list and becomes read-only until it is restored.`,
      confirmLabel: 'Archive',
      destructive: true,
    });
    if (!ok) return;
    setArchiving(true);
    setError(null);
    try {
      await apiFetch(`${projectPath}/archive`, { method: 'POST' });
      toast('Stream archived');
      await load();
    } catch (err) {
      const message = apiErrorMessage(err, 'Failed to archive stream');
      setError(message);
      toast(message);
    } finally {
      setArchiving(false);
    }
  }

  async function onUnarchive() {
    setArchiving(true);
    setError(null);
    try {
      await apiFetch(`${projectPath}/unarchive`, { method: 'POST' });
      toast('Stream restored');
      await load();
    } catch (err) {
      const message = apiErrorMessage(err, 'Failed to restore stream');
      setError(message);
      toast(message);
    } finally {
      setArchiving(false);
    }
  }

  if (loading) {
    return (
      <div>
        <PageTitle title="General" />
        <div className="max-w-lg space-y-4" aria-busy="true" aria-label="Loading">
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-9 w-32" />
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div>
        <PageTitle title="General" />
        <WorkPageError error={error ?? 'Stream not found.'} projectId={projectId} onJoined={() => void load()} />
      </div>
    );
  }

  return (
    <div>
      <PageTitle title="General" />
      <WorkPageError error={error} projectId={projectId} onJoined={() => void load()} />
      {archived ? (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-md border border-dashed px-4 py-3">
          <p className="text-sm text-muted-foreground">
            This stream is archived and read-only.
          </p>
          {canManage ? (
            <Button type="button" size="sm" variant="outline" disabled={archiving} onClick={() => void onUnarchive()}>
              {archiving ? 'Restoring...' : 'Restore stream'}
            </Button>
          ) : null}
        </div>
      ) : null}

      <form onSubmit={onSave} className="max-w-lg space-y-4">
        <div className="grid gap-2">
          <Label htmlFor="project-name">Name</Label>
          <Input
            id="project-name"
            value={name}
            disabled={!editable}
            maxLength={255}
            onChange={(event) => setName(event.target.value)}
          />
        </div>

        <div className="grid gap-2">
          <Label htmlFor="project-identifier">Identifier</Label>
          <Input
            id="project-identifier"
            value={project.identifier}
            readOnly
            disabled
            className="font-mono"
          />
          <p className="text-xs text-muted-foreground">
            Work items are keyed {project.identifier}-1, {project.identifier}-2, and so on. The
            identifier cannot be changed here because every existing link would break.
          </p>
        </div>

        <div className="grid gap-2">
          <Label htmlFor="project-description">Description</Label>
          <RichTextField
            variant="compact"
            value={description}
            readOnly={!editable}
            upload={(file) => uploadPendingAttachment(file).then((row) => row.download_url)}
            onUploadError={toast}
            onChange={setDescription}
          />
        </div>

        {editable ? (
          <div className="flex items-center gap-2">
            <Button type="submit" size="sm" disabled={saving || !dirty}>
              {saving ? 'Saving...' : 'Save changes'}
            </Button>
            {dirty ? (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => {
                  setName(project.name);
                  setDescription(project.description ?? '');
                }}
              >
                Discard
              </Button>
            ) : null}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            {archived
              ? 'Restore the stream to edit these settings.'
              : 'Only stream admins can change these settings.'}
          </p>
        )}
      </form>

      {canManage && !archived ? (
        <div className="mt-10 max-w-lg border-t pt-6">
          <h2 className="text-sm font-medium">Archive stream</h2>
          <p className="mt-1 mb-3 text-sm text-muted-foreground">
            Hide this stream and its work items from lists and boards. Nothing is deleted, and you
            can restore it later.
          </p>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={archiving}
            onClick={() => void onArchive()}
          >
            {archiving ? 'Archiving...' : 'Archive stream'}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
